import { useState } from "react";
import {
  Text,
  Group,
  Button,
  Container,
  Title,
  List,
  Card,
  ThemeIcon,
  Box,
  Alert,
  Badge,
  Stack,
  Progress,
  Collapse,
  Tooltip,
  SegmentedControl,
} from "@mantine/core";
import {
  IconFile,
  IconFileImport,
  IconCheck,
  IconAlertCircle,
  IconX,
  IconAlertTriangle,
  IconChevronDown,
  IconChevronRight,
  IconDownload,
} from "@tabler/icons-react";
import { useContext } from "react";

import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import {
  useGetTicketColumns,
  useImportTickets,
  useValidateTicketImport,
} from "../../api/tickets";
import { toBase64 } from "../../services/helper";
import CSVDropzoneButton from "../CSVDropzone";
import { useGetEvent } from "../../api/event";

const describeRow = (row) =>
  [[row?.firstname, row?.lastname].filter(Boolean).join(" "), row?.email]
    .filter(Boolean)
    .join(", ");

const FIELD_LABELS = {
  firstname: "first name",
  lastname: "last name",
  email: "email",
  companyname: "company",
  category: "category",
  checkedinat: "checked in",
};

// "lastName" or "lastname" -> "last name"; unknown columns as written.
const fieldLabel = (field) =>
  FIELD_LABELS[field.toLowerCase()] ??
  field.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();

// One validator error as plain words: "last name is required".
const describeError = (error) => {
  const rawField =
    error.params?.missingProperty ||
    error.instancePath?.replace(/^\//, "").split("/")[0];
  const field = rawField ? fieldLabel(rawField) : "a value";
  switch (error.keyword) {
    case "required":
      return `${field} is required`;
    case "minLength":
      return error.params?.limit > 1
        ? `${field} is too short`
        : `${field} is required`;
    case "maxLength":
      return `${field} is too long (at most ${error.params?.limit} characters)`;
    case "format":
    case "pattern":
      return rawField?.toLowerCase() === "email"
        ? "email is not a valid email address"
        : `${field} is not valid`;
    default:
      return `${field} is not valid`;
  }
};

const formatValidationError = ({ rowNumber, errors }) => {
  const reasons = [...new Set((errors || []).map(describeError))].join("; ");
  return `Row ${rowNumber}: ${reasons || "is not valid"}`;
};

const formatInsertError = ({ ticket, error }) =>
  `${describeRow(ticket) || "Row"}: ${error || "could not be imported"}`;

const formatRowNumbers = (rowNumbers) =>
  `${rowNumbers.length > 1 ? "Rows" : "Row"} ${rowNumbers.join(", ")}`;

const formatTagRows = (tag) =>
  `${tag.name}: ${tag.rowCount} ${tag.rowCount === 1 ? "row" : "rows"}`;

const BUILT_IN_COLUMNS = [
  "picture",
  "firstName",
  "lastName",
  "email",
  "category",
  "checkedInAt",
];

const escapeCsvCell = (value) =>
  /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const downloadTemplate = ({ columns, eventName }) => {
  const customColumns = (columns || [])
    .map((column) => column.name)
    .filter((name) => !BUILT_IN_COLUMNS.includes(name));
  const header = [
    "firstName",
    "lastName",
    "email",
    "companyName",
    "category",
    ...customColumns,
  ];
  const example = [
    "John",
    "Doe",
    "john.doe@example.com",
    "Acme",
    "Speaker;VIP",
    ...customColumns.map(() => ""),
  ];
  const csv = [header, example]
    .map((row) => row.map(escapeCsvCell).join(","))
    .join("\n");

  const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `import-template-${(eventName || "event").replace(/[\\/:*?"<>|]/g, "")}.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
};

const toFilePayload = async (file) => ({
  name: file.name,
  type: file.type,
  data: await toBase64(file),
});

function FileRequirements() {
  return (
    <Alert
      icon={<IconAlertCircle size={16} />}
      title="File Requirements"
      color="blue"
      variant="light"
      mt="md"
    >
      <List size="sm">
        <List.Item>File must be in .csv or .xlsx format</List.Item>
        <List.Item>File size must be less than 10MB</List.Item>
        <List.Item>Required columns: first name, last name</List.Item>
        <List.Item>
          Optional columns: email, used to send the ticket email. A ticket
          without one can still be added in the app with its code or link.
        </List.Item>
        <List.Item>
          Optional columns: category, one or more category tags separated by ;
          (e.g. Speaker;VIP)
        </List.Item>
      </List>
    </Alert>
  );
}

function FileDropzone({ isHardBlocked, onDrop }) {
  return (
    <Tooltip label="Attendee limit reached" disabled={!isHardBlocked}>
      <Box style={{ cursor: isHardBlocked ? "not-allowed" : "default" }}>
        <CSVDropzoneButton onDrop={onDrop} disabled={isHardBlocked} />
      </Box>
    </Tooltip>
  );
}

function SelectedFile({
  file,
  importLabel,
  canImport,
  isLoading,
  isImporting,
  onImport,
  onClear,
}) {
  return (
    <Stack gap="xs" align="center">
      <Group>
        <ThemeIcon size="xl" variant="light" radius="xl" color="blue">
          <IconFile size={24} />
        </ThemeIcon>
        <Stack gap={0}>
          <Text fw={500}>{file.name}</Text>
          <Text size="xs" c="dimmed">
            {(file.size / 1024 / 1024).toFixed(2)} MB
          </Text>
        </Stack>
      </Group>

      <Group mt={8} grow w="100%">
        <Button
          leftSection={<IconCheck size={16} />}
          onClick={onImport}
          color="blue"
          disabled={!canImport}
          loading={isLoading}
          fullWidth
        >
          {importLabel}
        </Button>
        <Button
          leftSection={<IconX size={16} />}
          onClick={onClear}
          variant="outline"
          color="red"
          disabled={isImporting}
          fullWidth
        >
          Clear File
        </Button>
      </Group>
    </Stack>
  );
}

function PreviewSection({ title, children }) {
  return (
    <Box>
      <Title order={6} mb="xs">
        {title}
      </Title>
      {children}
    </Box>
  );
}

function ImportPreview({ validation, tagResolution, onTagResolutionChange }) {
  const exceededBy = validation.attendeeLimit?.exceededBy || 0;
  const unknownTags = validation.tags.filter((tag) => !tag.exists && tag.valid);
  const invalidTags = validation.tags.filter(
    (tag) => !tag.exists && !tag.valid
  );
  const problems = [
    ...validation.validationErrors.map(formatValidationError),
    ...validation.duplicateEmails.map(
      ({ email, name, rowNumbers }) =>
        `${formatRowNumbers(rowNumbers)}: ${name} (${email}) is in the file more than once, only the first row is imported`
    ),
    ...validation.existingTickets.map(
      ({ email, name, rowNumbers }) =>
        `${formatRowNumbers(rowNumbers)}: ${name} (${email}) already has a ticket`
    ),
  ];

  return (
    <Card withBorder radius="md" mt="md">
      <Stack gap="sm">
        <Text size="sm">
          {validation.importable} of {validation.total} rows will be imported.
        </Text>

        {exceededBy > 0 && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title="Attendee limit exceeded"
          >
            This import exceeds the attendee limit by {exceededBy}. Remaining
            capacity is {validation.attendeeLimit.remaining}.
          </Alert>
        )}

        {problems.length > 0 && (
          <PreviewSection title="Rows with problems">
            {problems.map((problem, index) => (
              <Text key={index} size="sm" c="red">
                {problem}
              </Text>
            ))}
          </PreviewSection>
        )}

        {unknownTags.length > 0 && (
          <PreviewSection title="Category tags that don't exist yet">
            {unknownTags.map((tag) => (
              <Text key={tag.name} size="sm">
                {formatTagRows(tag)}
              </Text>
            ))}
            <SegmentedControl
              mt="xs"
              value={tagResolution}
              onChange={onTagResolutionChange}
              data={[
                { value: "skip", label: "Skip tags" },
                { value: "create", label: "Create tags" },
              ]}
            />
          </PreviewSection>
        )}

        {invalidTags.length > 0 && (
          <PreviewSection title="Invalid category tags, will be skipped">
            {invalidTags.map((tag) => (
              <Text key={tag.name} size="sm" c="red">
                {formatTagRows(tag)}
              </Text>
            ))}
          </PreviewSection>
        )}
      </Stack>
    </Card>
  );
}

function ImportErrorDetails({ validationErrors, insertErrors }) {
  const [showErrors, setShowErrors] = useState(false);

  return (
    <Card withBorder shadow="sm" radius="md" mt="md" bg="red.0">
      <Card.Section withBorder inheritPadding py="xs" bg="red.1">
        <Group justify="space-between">
          <Group>
            <ThemeIcon color="red" variant="light" size="md">
              <IconAlertTriangle size={16} />
            </ThemeIcon>
            <Text fw={500}>Error Details</Text>
          </Group>
          <Button
            variant="subtle"
            color="red"
            rightSection={
              showErrors ? (
                <IconChevronDown size={16} />
              ) : (
                <IconChevronRight size={16} />
              )
            }
            onClick={() => setShowErrors(!showErrors)}
          >
            {showErrors ? "Hide Details" : "Show Details"}
          </Button>
        </Group>
      </Card.Section>

      <Collapse expanded={showErrors}>
        <Box pt="xs">
          {validationErrors.length > 0 && (
            <>
              <Title order={6} mb="xs" mt="md">
                Validation Errors
              </Title>
              {validationErrors.map((error, index) => (
                <Text key={`validation-${index}`} size="sm" c="red">
                  {formatValidationError(error)}
                </Text>
              ))}
            </>
          )}

          {insertErrors.length > 0 && (
            <>
              <Title order={6} mb="xs" mt="md">
                Insert Errors
              </Title>
              {insertErrors.map((error, index) => (
                <Text key={`insert-${index}`} size="sm" c="red">
                  {formatInsertError(error)}
                </Text>
              ))}
            </>
          )}
        </Box>
      </Collapse>
    </Card>
  );
}

function ImportResult({ importData }) {
  const total = importData.total || 0;
  const successful = importData.successfullyInserted || 0;
  const validationErrors = importData.errors?.validationErrors || [];
  const insertErrors = importData.errors?.insertErrors || [];
  const createdTags = importData.tags?.created || [];
  const skippedTags = importData.tags?.skipped || [];
  const errorCount = validationErrors.length + insertErrors.length;

  return (
    <>
      <Alert
        icon={
          errorCount ? <IconAlertCircle size={16} /> : <IconCheck size={16} />
        }
        title={
          errorCount ? "Import Completed with Errors" : "Import Successful"
        }
        color={errorCount ? "yellow" : "green"}
        variant="light"
        mt="md"
      >
        <Stack gap="xs">
          <Text size="sm">
            Successfully imported {successful} out of {total} tickets.
          </Text>

          <Progress
            value={(successful / total) * 100}
            color={errorCount ? "yellow" : "green"}
          />

          {createdTags.length > 0 && (
            <Text size="sm">Created tags: {createdTags.join(", ")}</Text>
          )}

          {skippedTags.length > 0 && (
            <Text size="sm">
              Skipped unknown tags: {skippedTags.join(", ")}
            </Text>
          )}

          {errorCount > 0 && (
            <Group>
              <Badge color="red">
                {errorCount === 1 ? "1 error" : `${errorCount} errors`}
              </Badge>
              <Text size="xs" c="dimmed">
                Click on "Show Details" below to see error information
              </Text>
            </Group>
          )}
        </Stack>
      </Alert>

      {errorCount > 0 && (
        <ImportErrorDetails
          validationErrors={validationErrors}
          insertErrors={insertErrors}
        />
      )}
    </>
  );
}

function ImportExportTickets() {
  const { user } = useContext(AuthContext);
  const currentWorkspace = getCurrentWorkspace(user);
  const currentEventId = currentWorkspace.eventId;
  const [file, setFile] = useState(null);
  const [tagResolution, setTagResolution] = useState("skip");

  const { data: event } = useGetEvent(currentEventId);
  const { data: columns, isLoading: isLoadingColumns } = useGetTicketColumns({
    eventId: currentEventId,
  });
  const {
    mutate: importMutate,
    isPending: isImporting,
    data: importData,
    isSuccess: importSuccess,
    reset: resetImport,
  } = useImportTickets();
  const {
    mutate: validateMutate,
    isPending: isValidating,
    data: validation,
    reset: resetValidation,
  } = useValidateTicketImport();

  const canImport =
    !!validation?.importable &&
    !validation.attendeeLimit?.exceededBy &&
    !importSuccess;

  const handleDrop = async (droppedFile) => {
    setFile(droppedFile);
    validateMutate({
      file: await toFilePayload(droppedFile),
      eventId: currentEventId,
    });
  };

  const handleClear = () => {
    setFile(null);
    setTagResolution("skip");
    resetValidation();
    resetImport();
  };

  const handleImport = async () => {
    importMutate({
      file: await toFilePayload(file),
      eventId: currentEventId,
      tagResolution,
    });
  };

  return (
    <Container size="lg" mt="md">
      <Card shadow="sm" p="lg" radius="md" withBorder>
        <Card.Section withBorder inheritPadding py="xs" bg="blue.0">
          <Group justify="space-between">
            <Group>
              <ThemeIcon size="lg" variant="light" color="blue">
                <IconFileImport size={20} />
              </ThemeIcon>
              <Title order={4}>Import Tickets</Title>
            </Group>
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              loading={isLoadingColumns}
              onClick={() =>
                downloadTemplate({ columns, eventName: event?.name })
              }
            >
              Download template
            </Button>
          </Group>
        </Card.Section>

        <FileRequirements />

        <Box
          my="md"
          p="md"
          style={{
            border: "1px dashed var(--mantine-color-gray-4)",
            borderRadius: "8px",
            backgroundColor: "var(--mantine-color-gray-0)",
          }}
        >
          {!file ? (
            <FileDropzone
              isHardBlocked={event?.isHardBlocked}
              onDrop={handleDrop}
            />
          ) : (
            <SelectedFile
              file={file}
              importLabel={
                validation
                  ? `Import ${validation.importable} ${validation.importable === 1 ? "ticket" : "tickets"}`
                  : "Import File"
              }
              canImport={canImport}
              isLoading={isImporting || isValidating}
              isImporting={isImporting}
              onImport={handleImport}
              onClear={handleClear}
            />
          )}
        </Box>

        {validation && file && !importSuccess && (
          <ImportPreview
            validation={validation}
            tagResolution={tagResolution}
            onTagResolutionChange={setTagResolution}
          />
        )}

        {importData && importSuccess && file && (
          <ImportResult importData={importData} />
        )}
      </Card>
    </Container>
  );
}

export default ImportExportTickets;
